import { gameId, uuidPattern } from './gameplay.mjs';
import { adaptCampaignModel } from './activityCampaignModels.js';

// Recover only a frozen receipt; this flow never creates a claim or a request UUID.
export function createNewbiePackageRecovery({ recover, recoverRequest, identity, token, storage, changed = () => {} }) {
  let version = 0;
  let value = { visible: false, requestId: '', result: null, gifts: [], loading: false, error: '' };
  const publish = patch => { value = { ...value, ...patch }; changed(value); };
  function pause() { version++; publish({ visible: false, requestId: '', result: null, gifts: [], loading: false, error: '' }); }
  async function open(id) {
    pause();
    const activityId = gameId(id), actor = identity(), session = token(), sequence = version;
    if (!activityId || !actor || !session) return;
    const key = `kg_newbie_package:v1:${actor}:${activityId}`;
    const current = () => version === sequence && identity() === actor && token() === session;
    let pending;
    const giftKeys = ['newbie-deposit-gift','newbie-withdrawal-gift','newbie-first-game-gift','newbie-game-gift','newbie-revival-gift',...Array.from({length:20},(_,index)=>`newbie-first-game-gift-level-${index+1}`)];
    const giftPending = new Map();
    try { pending = storage.get(key); for (const giftKey of giftKeys) { const value = storage.get(`${key}:${giftKey}`); if (value) giftPending.set(giftKey,value); } }
    catch { publish({ visible: true, error: '上次礼包请求编号读取失败，请稍后重新核对' }); return; }
    if ((pending && (typeof pending !== 'string' || !uuidPattern.test(pending))) || [...giftPending.values()].some(value=>typeof value !== 'string' || !uuidPattern.test(value))) {
      publish({ visible: true, error: '上次礼包请求编号异常，请联系客服核对' }); return;
    }
    publish({ visible: Boolean(pending || giftPending.size), requestId: pending || giftPending.values().next().value || '', loading: true });
    try {
      const response = await recover(activityId);
      if (!current()) return;
      const model = adaptCampaignModel({ id: activityId, activityType: 'NEWBIE_TASK' }, response);
      const result = model.package?.claimed || model.package?.reviewStatus || model.package?.payoutStatus ? model.package : null;
      const gifts = (model.gifts || []).filter(gift=>gift.claimed || gift.reviewStatus || gift.payoutStatus);
      if (!result && !gifts.length && !giftPending.size) {
        if (pending || giftPending.size) publish({ error: '尚未查到原领取回执，请保留请求编号并稍后重新核对' });
        return;
      }
      if (pending && (!result || result.requestId.toLowerCase() !== pending.toLowerCase())) {
        publish({ error: '原领取请求编号不一致，请保留请求编号并联系客服核对' }); return;
      }
      for (const [giftKey,uuid] of giftPending) {
        const original = gifts.find(gift=>gift.key === giftKey);
        if (!original || original.requestId.toLowerCase() !== uuid.toLowerCase()) {
          const periodKey = storage.get(`${key}:${giftKey}:period`) || '';
          const restored = await recoverNewbiePending({ recoverRequest, id:activityId, target:giftKey, requestId:uuid, periodKey, today:response.today || '' });
          if (!current()) return;
          if (!restored) { publish({ visible:true, result, gifts, error:'独立礼包原请求尚待核对，请保留请求编号并稍后重试' }); return; }
          if (!restored.absentPastPeriod) { const index=gifts.findIndex(gift=>gift.key===giftKey); if(index>=0)gifts.splice(index,1); gifts.push(restored); }
        }
      }
      publish({ visible: Boolean(result || gifts.length), requestId: result?.requestId || gifts[0]?.requestId || '', result, gifts });
      // Only remove this member/activity's unchanged pending request after confirmation.
      if (pending && storage.get(key) === pending) storage.remove(key);
      for (const [giftKey,uuid] of giftPending) if (storage.get(`${key}:${giftKey}`) === uuid) { storage.remove(`${key}:${giftKey}`); storage.remove(`${key}:${giftKey}:period`); }
    } catch (error) {
      if (current() && (pending || giftPending.size)) publish({ error: '原礼包结果暂无法核对，请保留请求编号并稍后重试' });
    } finally {
      if (current()) publish({ loading: false });
    }
  }
  return { open, pause, get value() { return value; } };
}

// Only the server's locked read can retire a past-day unknown request. A current-day
// absence, a timeout or malformed receipt always retains the original UUID.
export async function recoverNewbiePending({ recoverRequest, id, target, requestId, periodKey = '', today = '' }) {
  if (!recoverRequest) return null;
  let receipt;
  try { receipt = await recoverRequest(id, requestId); }
  catch (error) {
    if (error?.errorCode === 'GAMEPLAY_RECORD_NOT_FOUND' && validNewbiePeriod(periodKey) && validNewbiePeriod(today) && periodKey < today) return { absentPastPeriod: true };
    throw error;
  }
  const originalTime = receipt?.createdAt ?? receipt?.completedAt;
  const validTime = value => Number.isSafeInteger(value) && value > 0 && value < 253402185600;
  if (String(receipt?.activityId) !== String(id) || receipt?.requestId?.toLowerCase() !== requestId.toLowerCase() || receipt?.targetKey !== target || !gameId(receipt?.recordId) || receipt.action !== 'CLAIM' || !/^\d+(?:\.\d{1,2})?$/.test(receipt.rewardAmount) || !validNewbiePeriod(receipt.periodKey) || periodKey && periodKey !== receipt.periodKey || !validTime(originalTime) || new Date((originalTime+8*3600)*1000).toISOString().slice(0,10) !== receipt.periodKey) throw Error('每日礼包原凭证待核对，请保留请求编号');
  // A definitive previous-day failure releases only the local old UUID; today's
  // independent quote stays unpaid and still requires an explicit user action.
  if (receipt.status === 'FAILED' && Number(receipt.rewardAmount) === 0 && !receipt.completedAt && validNewbiePeriod(today) && receipt.periodKey < today) {
    return { key: target, title: receipt.targetTitle, claimed: false, eligible: false, historicalFailed: true, reason: '原场馆指令明确失败，未发放奖励', requestId: receipt.requestId, recordId: String(receipt.recordId), completedAt: 0, rewardAmount: '0.00', periodKey: receipt.periodKey, taskCycle: 2 };
  }
  if (receipt.status !== 'COMPLETED' || Number(receipt.rewardAmount) <= 0 || !validTime(receipt.completedAt) || receipt.completedAt < originalTime) throw Error('每日礼包原凭证待核对，请保留请求编号');
  return { key: target, title: receipt.targetTitle, claimed: true, eligible: false, requestId: receipt.requestId, recordId: String(receipt.recordId), completedAt: receipt.completedAt, rewardAmount: receipt.rewardAmount, periodKey: receipt.periodKey, taskCycle: 2 };
}
export function validNewbiePeriod(value) {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const instant = Date.parse(value+'T00:00:00+08:00');
  return Number.isFinite(instant) && new Date(instant+8*3600*1000).toISOString().slice(0,10) === value;
}
